import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Building2, Check, CheckSquare, Copy, ExternalLink, Eye, FileClock, KeyRound, LockKeyhole, LogOut, Mail,
  MailCheck, LoaderCircle, MailWarning, MapPin, MessageCircle, Pencil, Phone, Search, ShieldCheck, Square, Upload,
  Scissors, UserRoundCheck, UsersRound, X,
} from "lucide-react";
import CropControls from "../components/CropControls";
import { Link } from "react-router-dom";
import MemberGate from "../components/MemberGate";
import { ErrorMessage, Loading } from "../components/Loading";
import { branches } from "../data/content";
import { useApi } from "../hooks/useApi";
import { canViewActivityLog } from "../lib/access";
import { api } from "../lib/api";
import { countryIsoFromName, countryOptions, phoneDetails } from "../lib/countries";
import { cropStyle, normalizeCrop } from "../lib/crop";

const indiaStateOptions = [
  "Andaman and Nicobar Islands", "Andhra Pradesh", "Arunachal Pradesh", "Assam", "Bihar", "Chandigarh",
  "Chhattisgarh", "Dadra and Nagar Haveli and Daman and Diu", "Delhi", "Goa", "Gujarat", "Haryana",
  "Himachal Pradesh", "Jammu and Kashmir", "Jharkhand", "Karnataka", "Kerala", "Ladakh", "Lakshadweep",
  "Madhya Pradesh", "Maharashtra", "Manipur", "Meghalaya", "Mizoram", "Nagaland", "Odisha", "Puducherry",
  "Punjab", "Rajasthan", "Sikkim", "Tamil Nadu", "Telangana", "Tripura", "Uttar Pradesh", "Uttarakhand",
  "West Bengal",
];

function editableMember(member) {
  return {
    ...member,
    currentCity: member.currentCity || "",
    currentState: member.currentState || "",
    currentCountry: member.currentCountry || "",
    phoneNumber: member.phoneNumber || "",
    phoneCountry: member.phoneCountry || countryIsoFromName(member.currentCountry),
    familyNote: member.familyNote || (member.children ? `Children: ${member.children}` : ""),
  };
}

function AdminPortraitManager({ member, type, onSaved }) {
  const cropKey = type === "college" ? "collegeCrop" : "currentCrop";
  const photoKey = type === "college" ? "college" : "current";
  const label = type === "college" ? "Original portrait" : "Newer portrait";
  const [crop, setCrop] = useState(() => normalizeCrop(member.photo?.[cropKey]));
  const [yearTaken, setYearTaken] = useState(String(member.photo?.currentYear || new Date().getFullYear()));
  const [saving, setSaving] = useState(false);
  const [feedback, setFeedback] = useState("");
  const src = `/api/members/${member.id}/photo?type=${type}&v=${encodeURIComponent(member.updatedAt || "")}`;
  const hasPhoto = Boolean(member.photo?.[photoKey]);

  useEffect(() => {
    setCrop(normalizeCrop(member.photo?.[cropKey]));
    if (type === "current") setYearTaken(String(member.photo?.currentYear || new Date().getFullYear()));
  }, [cropKey, member.photo, member.updatedAt, type]);

  async function uploadPortrait(event) {
    const file = event.currentTarget.files?.[0] || null;
    event.currentTarget.value = "";
    if (!file || saving) return;
    const currentYear = new Date().getFullYear();
    const year = type === "college" ? 1995 : Number(yearTaken);
    if (!Number.isInteger(year) || year < 1995 || year > currentYear) {
      setFeedback(`Enter a portrait year from 1995 to ${currentYear}.`);
      return;
    }
    setSaving(true);
    setFeedback(`Uploading ${label.toLowerCase()}…`);
    try {
      const body = new FormData();
      body.append("photo", file);
      body.append("type", type);
      body.append("yearTaken", String(year));
      const result = await api(`/api/members/${member.id}/photo`, { method: "POST", body });
      setFeedback(`${label} saved.`);
      onSaved(result.member);
    } catch (error) {
      setFeedback(error.message);
    } finally {
      setSaving(false);
    }
  }

  async function saveCrop() {
    setSaving(true);
    setFeedback("");
    try {
      const result = await api(`/api/admin/members/${member.id}/photo-crop`, {
        method: "PATCH",
        body: JSON.stringify({ type, crop }),
      });
      setFeedback("Crop saved.");
      onSaved(result.member);
    } catch (error) {
      setFeedback(error.message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <article className="admin-portrait-crop">
      <div className="admin-portrait-crop__preview">
        <span className="admin-portrait-crop__fallback" aria-hidden="true">{member.firstName?.[0]}{member.lastName?.[0]}</span>
        {hasPhoto && <img src={src} alt={`${label} crop preview`} style={cropStyle(crop)} />}
      </div>
      <div>
        <p className="kicker">{label}</p>
        {type === "current" && <label className="admin-portrait-upload__year">Year for uploaded portrait<input aria-label="Newer portrait upload year" type="number" min="1995" max={new Date().getFullYear()} value={yearTaken} onChange={(event) => setYearTaken(event.target.value)} disabled={saving} /></label>}
        <label className={saving ? "button button--small button--ghost is-disabled" : "button button--small button--ghost"} aria-disabled={saving}>
          <Upload aria-hidden="true" /> {hasPhoto ? `Replace ${type === "college" ? "original" : "newer"}` : `Upload ${type === "college" ? "original" : "newer"}`}
          <input className="sr-only" type="file" accept="image/jpeg,image/png,image/webp" onChange={uploadPortrait} disabled={saving} />
        </label>
        {hasPhoto && (
          <>
            <CropControls crop={crop} onChange={setCrop} compact />
            <button className="button button--small button--ink" type="button" onClick={saveCrop} disabled={saving}><Scissors aria-hidden="true" /> {saving ? "Saving…" : "Save crop"}</button>
          </>
        )}
        {feedback && <p className="admin-portrait-crop__feedback" role="status" aria-live="polite">{feedback}</p>}
      </div>
    </article>
  );
}

function ProfileEditor({ member, isAdministrator, isOwnProfile, onClose, onSaved, onPortraitSaved }) {
  const [form, setForm] = useState(() => editableMember(member));
  const [portrait, setPortrait] = useState(null);
  const [portraitYear, setPortraitYear] = useState(String(member.photo?.currentYear || new Date().getFullYear()));
  const [portraitSaving, setPortraitSaving] = useState(false);
  const [yearEditing, setYearEditing] = useState(false);
  const [portraitFeedback, setPortraitFeedback] = useState({
    state: "idle",
    message: member.photo?.current
      ? ""
      : isAdministrator ? "" : "Choose a portrait to save it automatically.",
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const name = `${member.firstName} ${member.lastName}`;
  const currentYear = new Date().getFullYear();
  const portraitPreview = useMemo(() => portrait ? URL.createObjectURL(portrait) : "", [portrait]);
  useEffect(() => () => { if (portraitPreview) URL.revokeObjectURL(portraitPreview); }, [portraitPreview]);
  const update = (key, value) => setForm((current) => ({ ...current, [key]: value }));

  async function uploadPortrait(file) {
    const year = Number(portraitYear);
    if (!Number.isInteger(year) || year < 1995 || year > currentYear) {
      setPortraitFeedback({ state: "error", message: `Enter a portrait year from 1995 to ${currentYear}.` });
      return;
    }
    setPortrait(file);
    setPortraitSaving(true);
    setPortraitFeedback({ state: "saving", message: "Uploading portrait…" });
    try {
      const body = new FormData();
      body.append("photo", file);
      body.append("yearTaken", portraitYear);
      const result = await api(`/api/members/${member.id}/photo`, { method: "POST", body });
      setPortraitYear(String(result.member.photo?.currentYear || year));
      setPortrait(null);
      setPortraitFeedback({ state: "saved", message: "Portrait saved automatically." });
      onPortraitSaved(result.member);
    } catch (requestError) {
      setPortrait(null);
      setPortraitFeedback({ state: "error", message: requestError.message });
    } finally {
      setPortraitSaving(false);
    }
  }

  function selectPortrait(event) {
    const file = event.currentTarget.files?.[0] || null;
    event.currentTarget.value = "";
    if (file) void uploadPortrait(file);
  }

  function changePortraitYear(value) {
    setPortraitYear(value);
  }

  async function savePortraitYear() {
    const year = Number(portraitYear);
    if (!Number.isInteger(year) || year < 1995 || year > currentYear) {
      setPortraitFeedback({ state: "error", message: `Enter a portrait year from 1995 to ${currentYear}.` });
      return;
    }
    setPortraitSaving(true);
    setPortraitFeedback({ state: "saving", message: "Saving portrait year…" });
    try {
      const result = await api(`/api/members/${member.id}`, {
        method: "PATCH",
        body: JSON.stringify({ currentPortraitYear: portraitYear }),
      });
      setPortraitYear(String(result.member.photo?.currentYear || year));
      setYearEditing(false);
      setPortraitFeedback({ state: "saved", message: "Portrait year saved." });
      onPortraitSaved(result.member);
    } catch (requestError) {
      setPortraitFeedback({ state: "error", message: requestError.message });
    } finally {
      setPortraitSaving(false);
    }
  }

  async function save(event) {
    event.preventDefault();
    if (portraitSaving) return;
    setSaving(true);
    setError("");
    try {
      const profile = {
        city: form.city || "",
        state: form.state || "",
        country: "India",
        birthday: form.birthday || "",
        currentRole: form.currentRole || "",
        company: form.company || "",
        currentCity: form.currentCity || "",
        currentState: form.currentState || "",
        currentCountry: form.currentCountry || "",
        email: form.email || "",
        phoneCountry: form.phoneCountry || "",
        phoneNumber: form.phoneNumber || "",
        bio: form.bio || "",
        spouse: form.spouse || "",
        familyNote: form.familyNote || "",
        website: form.website || "",
        linkedin: form.linkedin || "",
        instagram: form.instagram || "",
        facebook: form.facebook || "",
      };
      const result = await api(`/api/members/${member.id}`, { method: "PATCH", body: JSON.stringify(profile) });
      onSaved(result.member);
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="modal-backdrop" role="presentation" onMouseDown={(event) => event.target === event.currentTarget && onClose()}>
      <section className="profile-editor" role="dialog" aria-modal="true" aria-labelledby="profile-editor-title">
        <header className="profile-editor__header">
          <div><p className="kicker">{isAdministrator && !isOwnProfile ? "Administrator edit" : "My class record"}</p><h2 id="profile-editor-title">{name}</h2><span>{member.branch}</span></div>
          <button className="icon-button" type="button" onClick={onClose} aria-label="Close"><X aria-hidden="true" /></button>
        </header>
        <form className="profile-editor__form" onSubmit={save}>
          <section className="portrait-editor">
            <div className="portrait-editor__pair">
              <figure><span className="portrait-editor__fallback">{member.firstName?.[0]}{member.lastName?.[0]}</span>{member.photo?.college && <img src={`/api/members/${member.id}/photo?type=college&v=${encodeURIComponent(member.updatedAt || "")}`} alt={`${name} in the original 1995 class archive`} style={cropStyle(member.photo?.collegeCrop)} onError={(event) => { event.currentTarget.style.display = "none"; }} />}<figcaption><LockKeyhole aria-hidden="true" /> Original · 1995 · {isAdministrator ? "administrator managed" : "file locked"}</figcaption></figure>
              <figure><span className="portrait-editor__fallback">{member.firstName?.[0]}{member.lastName?.[0]}</span>{(portraitPreview || member.photo?.current) && <img src={portraitPreview || `/api/members/${member.id}/photo?type=current&v=${encodeURIComponent(member.updatedAt || "")}`} alt={`${name}, newer portrait`} style={portraitPreview ? undefined : cropStyle(member.photo?.currentCrop)} onError={(event) => { event.currentTarget.style.display = "none"; }} />}<figcaption>Newer portrait{portrait ? ` · ${portraitYear}` : member.photo?.current ? member.photo.currentYear ? ` · ${member.photo.currentYear}` : " · year not recorded" : " · not added"}</figcaption></figure>
            </div>
            <div className="portrait-editor__controls">
              <p className="kicker">Then / now</p><h3>{isAdministrator ? "Manage both portraits" : member.photo?.current ? "Update the newer portrait" : "Add a newer portrait"}</h3><p>{isAdministrator ? "Use the administrator portrait tools below to upload, replace, and crop either image. JPEG, PNG, and WebP files up to 8 MB are accepted." : "The original black-and-white portrait is permanently locked. A newer image is saved beside it, never over it. Use JPEG, PNG, or WebP up to 8 MB."}</p>
              {!isAdministrator && <label className={portraitSaving ? "button button--ghost is-disabled" : "button button--ghost"} aria-disabled={portraitSaving}><Upload aria-hidden="true" /> Choose portrait<input className="sr-only" type="file" accept="image/jpeg,image/png,image/webp" onChange={selectPortrait} disabled={portraitSaving} /></label>}
              {member.photo?.current ? (
                <div className="portrait-year-control">
                  <span>Portrait year <strong>{member.photo.currentYear || "Not recorded"}</strong></span>
                  {yearEditing ? (
                    <div><input aria-label="Newer portrait year" type="number" min="1995" max={currentYear} value={portraitYear} onChange={(event) => changePortraitYear(event.target.value)} disabled={portraitSaving} /><button className="button button--small button--ink" type="button" onClick={savePortraitYear} disabled={portraitSaving}>Save year</button><button className="text-link" type="button" onClick={() => { setPortraitYear(String(member.photo?.currentYear || currentYear)); setYearEditing(false); }}>Cancel</button></div>
                  ) : <button className="text-link" type="button" onClick={() => setYearEditing(true)}><Pencil aria-hidden="true" /> Edit year</button>}
                </div>
              ) : !isAdministrator ? (
                <label className="form-field">Year picture was taken<input type="number" min="1995" max={currentYear} value={portraitYear} onChange={(event) => changePortraitYear(event.target.value)} required disabled={portraitSaving} /></label>
              ) : null}
              {portraitFeedback.message && (
                <div className={`portrait-autosave is-${portraitFeedback.state}`} role={portraitFeedback.state === "error" ? "alert" : "status"} aria-live="polite">
                  {portraitFeedback.state === "saving" && <LoaderCircle aria-hidden="true" />}
                  {portraitFeedback.state === "saved" && <Check aria-hidden="true" />}
                  {portraitFeedback.state === "error" && <X aria-hidden="true" />}
                  <span>{portraitFeedback.message}</span>
                </div>
              )}
            </div>
          </section>
          {isAdministrator && (
            <section className="admin-portrait-crops">
              <div className="profile-edit-section__heading"><p className="kicker">Administrator portrait management</p><h3>Upload, replace, and crop.</h3><p>Administrators can manage both portraits for any classmate. Cropping changes only the presentation; the full uploaded image remains preserved.</p></div>
              <div className="admin-portrait-crops__grid">
                <AdminPortraitManager member={member} type="college" onSaved={onPortraitSaved} />
                <AdminPortraitManager member={member} type="current" onSaved={onPortraitSaved} />
              </div>
            </section>
          )}
          <section className="profile-edit-section">
            <div className="profile-edit-section__heading"><p className="kicker">Original class record</p><h3>The details we inherited.</h3><p>Correct these fields if the earlier alumni record needs an update.</p></div>
            <div className="form-grid form-grid--three">
              <label className="form-field">Original city<input value={form.city || ""} onChange={(event) => update("city", event.target.value)} /></label>
              <label className="form-field">Original state<select value={form.state || ""} onChange={(event) => update("state", event.target.value)}><option value="">Choose a state</option>{form.state && !indiaStateOptions.includes(form.state) && <option value={form.state}>{form.state}</option>}{indiaStateOptions.map((state) => <option value={state} key={state}>{state}</option>)}</select></label>
              <label className="form-field">Birthday<input type="date" value={form.birthday || ""} onChange={(event) => update("birthday", event.target.value)} /></label>
            </div>
          </section>
          <section className="profile-edit-section">
            <div className="profile-edit-section__heading"><p className="kicker">Where I am now</p><h3>Work, place, and a little context.</h3><p>All fields are optional.</p></div>
            <div className="form-grid form-grid--contact">
              <label className="form-field contact-field--role">Current role<input value={form.currentRole || ""} onChange={(event) => update("currentRole", event.target.value)} /></label>
              <label className="form-field contact-field--company">Company or organization<input value={form.company || ""} onChange={(event) => update("company", event.target.value)} /></label>
              <label className="form-field contact-field--city">Current city<input value={form.currentCity} onChange={(event) => update("currentCity", event.target.value)} /></label>
              <label className="form-field contact-field--state">Current state / region<input value={form.currentState} onChange={(event) => update("currentState", event.target.value)} /></label>
              <label className="form-field contact-field--country">Current country<select value={form.currentCountry} onChange={(event) => update("currentCountry", event.target.value)}><option value="">Choose a country</option>{countryOptions.map((country) => <option value={country.iso} key={country.iso}>{country.name}</option>)}</select></label>
              <label className="form-field contact-field--email">Email address<input type="email" value={form.email || ""} onChange={(event) => update("email", event.target.value)} /></label>
              <label className="form-field contact-field--phone-country">Phone country code<select value={form.phoneCountry} onChange={(event) => update("phoneCountry", event.target.value)}><option value="">Choose a country code</option>{countryOptions.map((country) => <option value={country.iso} key={country.iso}>{country.name} (+{country.callingCode})</option>)}</select></label>
              <label className="form-field contact-field--phone">Phone / WhatsApp number<input inputMode="tel" value={form.phoneNumber} onChange={(event) => update("phoneNumber", event.target.value)} /></label>
            </div>
          </section>
          <section className="profile-edit-section profile-edit-section--about">
            <div className="profile-edit-section__heading"><p className="kicker">About me</p><h3>The journey since MACE.</h3><p>Share the personal and professional chapters you would like classmates to know about.</p></div>
            <div className="form-grid">
              <label className="form-field form-field--wide">My story<textarea rows="8" maxLength="2000" value={form.bio || ""} onChange={(event) => update("bio", event.target.value)} placeholder="Where life took you, the work that matters to you, milestones, interests, lessons, or anything else you would like to share…" /><small>{(form.bio || "").length} / 2000 characters</small></label>
            </div>
          </section>
          <section className="profile-edit-section"><div className="profile-edit-section__heading"><p className="kicker">Family</p><h3>Who shares the story now.</h3><p>Optional details for reconnecting.</p></div><div className="form-grid"><label className="form-field">Spouse&apos;s name<input value={form.spouse || ""} onChange={(event) => update("spouse", event.target.value)} /></label><label className="form-field form-field--wide">A note about the family<textarea rows="3" value={form.familyNote} onChange={(event) => update("familyNote", event.target.value)} /></label></div></section>
          <section className="profile-edit-section"><div className="profile-edit-section__heading"><p className="kicker">Social profiles</p><h3>Make the next hello easier.</h3><p>Paste any complete public profile URLs you would like to share. Every field is optional.</p></div><div className="form-grid"><label className="form-field form-field--wide">LinkedIn profile<input type="url" value={form.linkedin || ""} onChange={(event) => update("linkedin", event.target.value)} placeholder="https://www.linkedin.com/in/…" /></label><label className="form-field">Instagram profile<input type="url" value={form.instagram || ""} onChange={(event) => update("instagram", event.target.value)} placeholder="https://www.instagram.com/…" /></label><label className="form-field">Facebook profile<input type="url" value={form.facebook || ""} onChange={(event) => update("facebook", event.target.value)} placeholder="https://www.facebook.com/…" /></label><label className="form-field form-field--wide">Personal website<input type="url" value={form.website || ""} onChange={(event) => update("website", event.target.value)} placeholder="https://…" /></label></div></section>
          <p className="privacy-note privacy-note--profile"><ShieldCheck aria-hidden="true" /> These profile details and portraits can be viewed only by people signed in to the member room.</p>
          {error && <ErrorMessage>{error}</ErrorMessage>}
          <div className="profile-editor__actions"><button className="button button--ghost" type="button" onClick={onClose}>Cancel</button><button className="button button--coral" type="submit" disabled={saving || portraitSaving}>{saving ? "Saving…" : portraitSaving ? "Saving portrait…" : "Save changes"} <Check aria-hidden="true" /></button></div>
        </form>
      </section>
    </div>
  );
}

function countryName(value) {
  const normalized = String(value || "").trim();
  return countryOptions.find((option) => option.iso === normalized.toUpperCase())?.name || normalized;
}

function birthdayLabel(value) {
  const match = String(value || "").match(/^(\d{4})-(\d{2})-(\d{2})$/u);
  if (!match) return value || "";
  const date = new Date(Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3])));
  if (Number.isNaN(date.valueOf())) return value;
  return new Intl.DateTimeFormat("en", {
    day: "numeric",
    month: "long",
    year: "numeric",
    timeZone: "UTC",
  }).format(date);
}

function safeProfileUrl(value) {
  try {
    const url = new URL(String(value || ""));
    return ["http:", "https:"].includes(url.protocol) ? url.href : "";
  } catch {
    return "";
  }
}

function ProfileFact({ label, children, wide = false }) {
  return (
    <div className={wide ? "profile-fact is-wide" : "profile-fact"}>
      <dt>{label}</dt>
      <dd>{children || <span className="profile-fact__empty">Not recorded</span>}</dd>
    </div>
  );
}

function ProfileViewer({ member, onClose }) {
  const name = `${member.firstName} ${member.lastName}`;
  const initials = `${member.firstName?.[0] || ""}${member.lastName?.[0] || ""}`;
  const phone = phoneDetails(member);
  const phoneDisplay = phone?.display || member.phoneNumber || member.phone || "";
  const socialProfiles = [
    ["LinkedIn", member.linkedin],
    ["Instagram", member.instagram],
    ["Facebook", member.facebook],
    ["Personal website", member.website],
  ].map(([label, value]) => [label, safeProfileUrl(value)]).filter(([, url]) => url);

  return (
    <div className="modal-backdrop" role="presentation" onMouseDown={(event) => event.target === event.currentTarget && onClose()}>
      <section className="profile-editor profile-viewer" role="dialog" aria-modal="true" aria-labelledby="profile-viewer-title">
        <header className="profile-editor__header">
          <div><p className="kicker">Read-only class profile</p><h2 id="profile-viewer-title">{name}</h2><span>{member.branch}</span></div>
          <button className="icon-button" type="button" onClick={onClose} aria-label="Close"><X aria-hidden="true" /></button>
        </header>
        <div className="profile-viewer__body">
          <p className="profile-viewer__notice"><LockKeyhole aria-hidden="true" /> This profile is available to signed-in class members and cannot be edited with the shared passphrase.</p>

          <section className="portrait-comparison">
            <div className="profile-section__heading">
              <div><p className="kicker">Then / now</p><h3>Two portraits, one story.</h3></div>
              <p>The original class photograph remains beside the newer portrait supplied by the member.</p>
            </div>
            <div className="portrait-pair">
              <figure className="portrait-card portrait-card--archive">
                <div className="portrait-image">
                  <div className="portrait-image__missing"><span>{initials}</span><small>Original portrait not available</small></div>
                  {member.photo?.college && <img src={`/api/members/${member.id}/photo?type=college&v=${encodeURIComponent(member.updatedAt || "")}`} alt={`${name} in the original 1995 class archive`} style={cropStyle(member.photo?.collegeCrop)} onError={(event) => { event.currentTarget.style.display = "none"; }} />}
                </div>
                <figcaption><span className="portrait-card__label"><LockKeyhole aria-hidden="true" /> Original class portrait</span><strong>1995</strong><small>Permanent archive photograph</small></figcaption>
              </figure>
              <figure className="portrait-card portrait-card--current">
                <div className="portrait-image">
                  <div className="portrait-image__missing"><span>{initials}</span><small>Newer portrait not added</small></div>
                  {member.photo?.current && <img src={`/api/members/${member.id}/photo?type=current&v=${encodeURIComponent(member.updatedAt || "")}`} alt={`${name}, newer portrait`} style={cropStyle(member.photo?.currentCrop)} onError={(event) => { event.currentTarget.style.display = "none"; }} />}
                </div>
                <figcaption><span className="portrait-card__label">Newer portrait</span><strong>{member.photo?.currentYear || "Year not recorded"}</strong><small>{member.photo?.current ? "Member-supplied photograph" : "No newer photograph on file"}</small></figcaption>
              </figure>
            </div>
          </section>

          <section className="profile-section">
            <div className="profile-section__heading">
              <div><p className="kicker">Original class record</p><h3>Where the story began.</h3></div>
              <p>Details carried forward from the alumni register.</p>
            </div>
            <dl className="profile-facts">
              <ProfileFact label="Branch">{member.branch}</ProfileFact>
              <ProfileFact label="Original city">{member.city}</ProfileFact>
              <ProfileFact label="Original state">{member.state}</ProfileFact>
              <ProfileFact label="Original country">{countryName(member.country)}</ProfileFact>
              <ProfileFact label="Birthday">{birthdayLabel(member.birthday)}</ProfileFact>
              <ProfileFact label="Reunion status">{member.reunionStatus}</ProfileFact>
              {member.address && <ProfileFact label="Original address" wide>{member.address}</ProfileFact>}
            </dl>
          </section>

          <section className="profile-section">
            <div className="profile-section__heading">
              <div><p className="kicker">Where they are now</p><h3>Work, place, and contact.</h3></div>
              <p>The current details shared in the private class directory.</p>
            </div>
            <dl className="profile-facts">
              <ProfileFact label="Current role">{member.currentRole}</ProfileFact>
              <ProfileFact label="Company or organization">{member.company}</ProfileFact>
              <ProfileFact label="Current city">{member.currentCity}</ProfileFact>
              <ProfileFact label="Current state / region">{member.currentState}</ProfileFact>
              <ProfileFact label="Current country">{countryName(member.currentCountry)}</ProfileFact>
              <ProfileFact label="Email address">{member.email && <a href={`mailto:${member.email}`}><Mail aria-hidden="true" /> {member.email}</a>}</ProfileFact>
              <ProfileFact label="Phone / WhatsApp" wide>{phoneDisplay && <span className="profile-contact-links">{phone?.telHref ? <a href={phone.telHref}><Phone aria-hidden="true" /> {phoneDisplay}</a> : phoneDisplay}{phone?.whatsappHref && <a href={phone.whatsappHref} target="_blank" rel="noreferrer"><MessageCircle aria-hidden="true" /> WhatsApp</a>}</span>}</ProfileFact>
            </dl>
          </section>

          <section className="profile-section">
            <div className="profile-section__heading">
              <div><p className="kicker">About</p><h3>The journey since MACE.</h3></div>
              <p>A personal and professional update in the member’s own words.</p>
            </div>
            <p className={member.bio ? "profile-story" : "profile-story is-empty"}>{member.bio || "No story has been added yet."}</p>
          </section>

          <section className="profile-section">
            <div className="profile-section__heading">
              <div><p className="kicker">Family</p><h3>Who shares the story now.</h3></div>
              <p>Family details included in the class profile.</p>
            </div>
            <dl className="profile-facts">
              <ProfileFact label="Spouse">{member.spouse}</ProfileFact>
              <ProfileFact label="Children">{member.children}</ProfileFact>
              <ProfileFact label="Family note" wide>{member.familyNote}</ProfileFact>
            </dl>
          </section>

          <section className="profile-section profile-section--social">
            <div className="profile-section__heading">
              <div><p className="kicker">Social profiles</p><h3>Continue the conversation.</h3></div>
              <p>Public links supplied by the member.</p>
            </div>
            {socialProfiles.length ? <div className="profile-links">{socialProfiles.map(([label, url]) => <a className="button button--ghost" href={url} target="_blank" rel="noreferrer" key={label}>{label} <ExternalLink aria-hidden="true" /></a>)}</div> : <p className="profile-story is-empty">No social profiles have been added.</p>}
          </section>
        </div>
      </section>
    </div>
  );
}

function ClaimProfile({ member, onClose, onClaimed }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmation, setConfirmation] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const name = `${member.firstName} ${member.lastName}`;

  async function claim(event) {
    event.preventDefault();
    setError("");
    if (password !== confirmation) {
      setError("The passwords do not match.");
      return;
    }
    setSubmitting(true);
    try {
      const result = await api(`/api/members/${member.id}/claim`, {
        method: "POST",
        body: JSON.stringify({ email, password }),
      });
      await onClaimed(result);
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="modal-backdrop" role="presentation" onMouseDown={(event) => event.target === event.currentTarget && onClose()}>
      <section className="modal-panel claim-profile" role="dialog" aria-modal="true" aria-labelledby="claim-profile-title">
        <button className="icon-button modal-close" type="button" onClick={onClose} aria-label="Close"><X aria-hidden="true" /></button>
        <div className="claim-profile__icon"><UserRoundCheck aria-hidden="true" /></div>
        <p className="kicker">Unclaimed class profile</p>
        <h2 id="claim-profile-title">Claim {name}.</h2>
        <p className="modal-intro">Use your email address and create a password. This email will be added to the profile and used for future sign-ins.</p>
        <p className="role-notice"><ShieldCheck aria-hidden="true" /> This creates a classmate account. It does not provide administrator access.</p>
        <form className="claim-profile__form form-grid" onSubmit={claim}>
          <label className="form-field form-field--wide">Email address<input type="email" autoComplete="email" value={email} onChange={(event) => setEmail(event.target.value)} required /></label>
          <label className="form-field">Password<input type="password" autoComplete="new-password" minLength="12" value={password} onChange={(event) => setPassword(event.target.value)} required /><small>Use at least 12 characters.</small></label>
          <label className="form-field">Confirm password<input type="password" autoComplete="new-password" minLength="12" value={confirmation} onChange={(event) => setConfirmation(event.target.value)} required /></label>
          {error && <ErrorMessage>{error}</ErrorMessage>}
          <div className="claim-profile__actions"><button className="button button--ghost" type="button" onClick={onClose}>Cancel</button><button className="button button--coral" type="submit" disabled={submitting}>{submitting ? "Creating account…" : "Claim my profile"} <UserRoundCheck aria-hidden="true" /></button></div>
        </form>
      </section>
    </div>
  );
}

function statusLabel(status) {
  if (status === "active") return "Active";
  if (status === "invited") return "Invite pending";
  return "Not provisioned";
}

function AccessManager({ onClose, currentMemberId }) {
  const loader = useCallback(() => api("/api/admin/accounts"), []);
  const { data, error, loading, reload } = useApi(loader, [loader]);
  const [query, setQuery] = useState("");
  const [role, setRole] = useState("classmate");
  const [selected, setSelected] = useState(() => new Set());
  const [submitting, setSubmitting] = useState(false);
  const [actionError, setActionError] = useState("");
  const [provisioned, setProvisioned] = useState([]);
  const [resetCandidate, setResetCandidate] = useState(null);
  const [resetting, setResetting] = useState(false);
  const accounts = data?.accounts || [];
  const filtered = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return accounts.filter((account) => !needle || [account.firstName, account.lastName, account.branch, account.profileEmail, account.loginEmail, account.status, account.role].join(" ").toLowerCase().includes(needle));
  }, [accounts, query]);
  const filteredIds = filtered.map((account) => account.memberId);
  const allFilteredSelected = filteredIds.length > 0 && filteredIds.every((id) => selected.has(id));
  const activationResults = provisioned.filter((result) => result.activationUrl);
  const emailedResults = activationResults.filter((result) => result.emailStatus === "sent");
  const manualResults = activationResults.filter((result) => result.emailStatus !== "sent");
  const passwordResetResults = activationResults.filter((result) => result.kind === "password-reset");

  function toggle(memberId) {
    setSelected((current) => {
      const next = new Set(current);
      if (next.has(memberId)) next.delete(memberId);
      else if (next.size < 100) next.add(memberId);
      return next;
    });
  }

  function toggleFiltered() {
    setSelected((current) => {
      const next = new Set(current);
      if (allFilteredSelected) filteredIds.forEach((id) => next.delete(id));
      else filteredIds.forEach((id) => { if (next.size < 100) next.add(id); });
      return next;
    });
  }

  async function provision() {
    setSubmitting(true);
    setActionError("");
    setProvisioned([]);
    try {
      const result = await api("/api/admin/provision", { method: "POST", body: JSON.stringify({ memberIds: [...selected], role }) });
      setProvisioned(result.results || []);
      setSelected(new Set());
      await reload();
    } catch (requestError) {
      setActionError(requestError.message);
    } finally {
      setSubmitting(false);
    }
  }

  async function copyInvitations() {
    const text = activationResults.map((result) => `${result.name}\n${result.activationUrl}`).join("\n\n");
    await navigator.clipboard.writeText(text);
  }

  async function resetPassword() {
    if (!resetCandidate) return;
    setResetting(true);
    setActionError("");
    setProvisioned([]);
    try {
      const response = await api(`/api/admin/accounts/${resetCandidate.memberId}/reset-password`, { method: "POST" });
      setProvisioned(response.result ? [response.result] : []);
      setResetCandidate(null);
      await reload();
    } catch (requestError) {
      setActionError(requestError.message);
    } finally {
      setResetting(false);
    }
  }

  return (
    <div className="modal-backdrop" role="presentation" onMouseDown={(event) => event.target === event.currentTarget && onClose()}>
      <section className="access-manager" role="dialog" aria-modal="true" aria-labelledby="access-manager-title">
        <header className="access-manager__header">
          <div><p className="kicker">Administrator tools</p><h2 id="access-manager-title">Manage class access</h2><p>Create individual accounts in batches of up to 100. New invitations expire after seven days.</p></div>
          <button className="icon-button" type="button" onClick={onClose} aria-label="Close"><X aria-hidden="true" /></button>
        </header>
        {resetCandidate && (
          <section className="reset-confirmation" role="alertdialog" aria-labelledby="reset-confirmation-title" aria-describedby="reset-confirmation-copy">
            <div className="reset-confirmation__card">
              <div><p className="kicker">Confirm password reset</p><h3 id="reset-confirmation-title">Reset {resetCandidate.firstName} {resetCandidate.lastName}?</h3><p id="reset-confirmation-copy">The current password and signed-in sessions will stop working immediately. Share the new one-time link with this member.</p></div>
              <div className="reset-confirmation__actions"><button className="button button--ghost" type="button" onClick={() => setResetCandidate(null)} disabled={resetting}>Cancel</button><button className="button button--coral" type="button" onClick={resetPassword} disabled={resetting}>{resetting ? "Resetting…" : "Reset password"} <KeyRound aria-hidden="true" /></button></div>
            </div>
          </section>
        )}
        {provisioned.length > 0 && (
          <section className="invitation-results" aria-live="polite">
            <div><p className="kicker">{passwordResetResults.length ? "Password reset ready" : "Provisioning complete"}</p><h3>{emailedResults.length ? `${emailedResults.length} ${passwordResetResults.length ? "reset" : "activation"} email${emailedResults.length === 1 ? "" : "s"} sent` : activationResults.length ? `${activationResults.length} ${passwordResetResults.length ? "reset" : "activation"} link${activationResults.length === 1 ? "" : "s"} ready` : "Roles updated"}</h3><p>{manualResults.length ? `${manualResults.length} link${manualResults.length === 1 ? " needs" : "s need"} to be shared manually. ` : ""}Each link can be used once and expires after seven days.</p></div>
            {activationResults.length > 0 && <button className="button button--ink" type="button" onClick={copyInvitations}>Copy all links <Copy aria-hidden="true" /></button>}
            {activationResults.length > 0 && <div className="invitation-links">{activationResults.map((result) => <div key={result.memberId}><span className="invitation-link__person"><b>{result.name}</b><small className={`email-delivery is-${result.emailStatus || "failed"}`}>{result.emailStatus === "sent" ? <><MailCheck aria-hidden="true" /> Sent to {result.suggestedEmail}</> : result.emailStatus === "unavailable" ? <><MailWarning aria-hidden="true" /> No email on file</> : result.emailStatus === "manual" ? <><MailWarning aria-hidden="true" /> Send manually to {result.suggestedEmail}</> : <><MailWarning aria-hidden="true" /> Email failed — share link</>}</small></span><code>{result.activationUrl}</code><button className="icon-button" type="button" aria-label={`Copy activation link for ${result.name}`} onClick={() => navigator.clipboard.writeText(result.activationUrl)}><Copy aria-hidden="true" /></button></div>)}</div>}
          </section>
        )}
        <div className="access-manager__toolbar">
          <label className="search-field"><Search aria-hidden="true" /><span className="sr-only">Search class accounts</span><input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Search names, branch, email…" /></label>
          <button className="button button--ghost" type="button" onClick={toggleFiltered}>{allFilteredSelected ? <CheckSquare aria-hidden="true" /> : <Square aria-hidden="true" />} {allFilteredSelected ? "Clear filtered" : "Select filtered"}</button>
        </div>
        {loading && <Loading label="Opening account register" />}
        {error && <ErrorMessage>{error}</ErrorMessage>}
        <div className="account-list">
          {filtered.map((account) => (
            <div className="account-row" key={account.memberId}>
              <label className="account-row__select"><span className="sr-only">Select {account.firstName} {account.lastName}</span><input type="checkbox" checked={selected.has(account.memberId)} onChange={() => toggle(account.memberId)} /></label>
              <span className="account-row__name"><b>{account.firstName} {account.lastName}</b><small>{account.branch}</small></span>
              <span className="account-row__email">{account.loginEmail || account.profileEmail || "No email on file"}</span>
              <span className={`status-badge is-${account.resetPending ? "reset" : account.status}`}>{account.resetPending ? "Reset requested" : statusLabel(account.status)}</span>
              <span className={`role-badge is-${account.role}`}>{account.role === "administrator" ? "Administrator" : "Classmate"}</span>
              {account.status !== "not_provisioned"
                ? <button className="button button--small account-row__reset" type="button" onClick={() => setResetCandidate(account)} disabled={account.memberId === currentMemberId} title={account.memberId === currentMemberId ? "Use Forgot password to reset your own account" : undefined}>Reset password <KeyRound aria-hidden="true" /></button>
                : <span className="account-row__empty-action" />}
            </div>
          ))}
        </div>
        <footer className="access-manager__actions">
          <span><strong>{selected.size}</strong> selected</span>
          <label>Access level<select value={role} onChange={(event) => setRole(event.target.value)}><option value="classmate">Classmate</option><option value="administrator">Administrator</option></select></label>
          <button className="button button--coral" type="button" onClick={provision} disabled={!selected.size || submitting}>{submitting ? "Provisioning…" : role === "administrator" ? "Provision as administrators" : "Provision as classmates"} <KeyRound aria-hidden="true" /></button>
        </footer>
        {actionError && <ErrorMessage>{actionError}</ErrorMessage>}
      </section>
    </div>
  );
}

function memberLocation(member) {
  const countryCode = member.currentCountry || "";
  const countryName = countryOptions.find((option) => option.iso === countryCode)?.name || member.country || countryCode;
  return [member.currentCity || member.city, countryName].filter(Boolean).join(", ");
}

export default function Members({ session, onLogin, onLogout }) {
  const authenticated = Boolean(session?.authenticated);
  const user = session?.user;
  const isAdministrator = user?.role === "administrator";
  const canViewActivity = canViewActivityLog(user);
  const isViewer = user?.role === "viewer";
  const [query, setQuery] = useState("");
  const [branch, setBranch] = useState("All");
  const [selected, setSelected] = useState(null);
  const [claiming, setClaiming] = useState(null);
  const [managingAccess, setManagingAccess] = useState(false);
  const loader = useCallback(() => authenticated ? api("/api/members") : Promise.resolve({ members: [] }), [authenticated]);
  const { data, error, loading, reload, setData } = useApi(loader, [loader]);
  const members = data?.members || [];
  const filtered = useMemo(() => {
    const needle = query.toLowerCase().trim();
    return members.filter((member) => {
      const matchesBranch = branch === "All" || member.branch === branch;
      const haystack = [member.firstName, member.lastName, member.city, member.currentCity, member.state, member.currentState, member.country, member.currentCountry, member.company, member.currentRole].join(" ").toLowerCase();
      return matchesBranch && (!needle || haystack.includes(needle));
    });
  }, [members, query, branch]);

  async function logout() {
    await api("/api/logout", { method: "POST" });
    onLogout();
  }

  const keepEditorOpen = useCallback((updatedMember) => {
    setData((current) => current ? {
      ...current,
      members: (current.members || []).map((item) => item.id === updatedMember.id ? updatedMember : item),
    } : current);
    setSelected((current) => current?.id === updatedMember.id ? updatedMember : current);
  }, [setData]);

  if (!authenticated) {
    return (
      <section className="members-locked">
        <div className="members-locked__art" aria-hidden="true">
          <p className="kicker">Class register / 1995</p><div className="members-locked__number">304</div>
          <div className="members-locked__caption"><strong>Names remembered.</strong><span>Eight branches.<br />One class.</span></div>
          <span className="members-locked__index">MACE / 91—95</span>
        </div>
        <MemberGate onSuccess={onLogin} />
      </section>
    );
  }

  return (
    <>
      <section className="page-hero page-hero--members">
        <div><p className="kicker">The private class directory</p><h1>Who we were.<br />Who we are now.</h1></div>
        <div className="page-hero__aside">
          <p>{isAdministrator
            ? "You can update any class record and provision individual access for classmates and other administrators."
            : isViewer
              ? "Shared passphrase access is read-only. You can open every class profile to see its full details and both portraits."
              : "Search the class directory and keep your own record up to date."}</p>
          <div className="member-session-actions">
            {isAdministrator && <button className="button button--yellow" type="button" onClick={() => setManagingAccess(true)}>Manage access <UsersRound aria-hidden="true" /></button>}
            {canViewActivity && <Link className="button button--cream" to="/admin/activity">View change log <FileClock aria-hidden="true" /></Link>}
            <button className="text-link" type="button" onClick={logout}>Sign out <LogOut aria-hidden="true" /></button>
          </div>
        </div>
      </section>
      <section className="directory section-wrap">
        <div className="directory-toolbar">
          <label className="search-field"><Search aria-hidden="true" /><span className="sr-only">Search members</span><input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Search names, cities, work…" /></label>
          <label className="select-field"><span className="sr-only">Filter by branch</span><select value={branch} onChange={(event) => setBranch(event.target.value)}><option>All</option>{branches.map((item) => <option key={item}>{item}</option>)}</select></label>
          <span className="directory-count">{filtered.length} / {members.length}</span>
        </div>
        {loading && <Loading label="Opening the class register" />}
        {error && <ErrorMessage>{error}</ErrorMessage>}
        <div className="member-list">
          {filtered.map((member, index) => {
            const name = `${member.firstName} ${member.lastName}`;
            const location = memberLocation(member);
            const phone = phoneDetails(member);
            const isOwnProfile = user?.role === "classmate" && user?.memberId === member.id;
            const canEdit = isAdministrator || isOwnProfile;
            return (
              <article className={isOwnProfile ? "member-row is-current" : "member-row"} key={member.id} style={{ "--i": index % 12 }}>
                <div className="member-row__index">{String(index + 1).padStart(3, "0")}</div>
                <div className={member.photo?.current ? "member-row__portraits has-current" : "member-row__portraits"}>
                  <div className="member-row__portrait is-college">
                    <span aria-hidden="true">{member.firstName?.[0]}{member.lastName?.[0]}</span>
                    {member.photo?.college && <img src={`/api/members/${member.id}/photo?type=college&v=${encodeURIComponent(member.updatedAt || "")}`} alt={`${name}, original 1995 portrait`} style={cropStyle(member.photo?.collegeCrop)} onError={(event) => { event.currentTarget.style.display = "none"; }} />}
                  </div>
                  {member.photo?.current && (
                    <div className="member-row__portrait is-current">
                      <span aria-hidden="true">{member.firstName?.[0]}{member.lastName?.[0]}</span>
                      <img src={`/api/members/${member.id}/photo?type=current&v=${encodeURIComponent(member.updatedAt || "")}`} alt={`${name}, newer portrait${member.photo.currentYear ? ` from ${member.photo.currentYear}` : ""}`} style={cropStyle(member.photo?.currentCrop)} onError={(event) => { event.currentTarget.style.display = "none"; }} />
                    </div>
                  )}
                </div>
                <div className="member-row__name"><h2>{name} {isOwnProfile && <small>YOU</small>}</h2><span>{member.branch}</span></div>
                <div className="member-row__detail">{location && <span><MapPin aria-hidden="true" /> {location}</span>}{(member.currentRole || member.company) && <span><Building2 aria-hidden="true" /> {[member.currentRole, member.company].filter(Boolean).join(" · ")}</span>}{phone && <span className="member-row__contact"><a href={phone.telHref}><Phone aria-hidden="true" /> {phone.display}</a>{phone.whatsappHref && <a href={phone.whatsappHref} target="_blank" rel="noreferrer"><MessageCircle aria-hidden="true" /> WhatsApp</a>}</span>}</div>
                <div className="member-row__links">
                  {member.linkedin && <a className="icon-button" href={member.linkedin} target="_blank" rel="noreferrer" aria-label={`${name} on LinkedIn`}><ExternalLink aria-hidden="true" /></a>}
                  {isViewer && member.claimable && <button className="button button--small button--claim" type="button" onClick={() => setClaiming(member)}>Claim my profile <UserRoundCheck aria-hidden="true" /></button>}
                  <button className="button button--small" type="button" onClick={() => setSelected(member)}>{canEdit ? isOwnProfile ? "Edit my profile" : "View / edit" : "View profile"} {canEdit ? <Pencil aria-hidden="true" /> : <Eye aria-hidden="true" />}</button>
                </div>
              </article>
            );
          })}
        </div>
      </section>
      {selected && (isAdministrator || (user?.role === "classmate" && user?.memberId === selected.id)
        ? <ProfileEditor member={selected} isAdministrator={isAdministrator} isOwnProfile={user?.memberId === selected.id} onClose={() => setSelected(null)} onPortraitSaved={keepEditorOpen} onSaved={(member) => { setData({ members: members.map((item) => item.id === member.id ? member : item) }); setSelected(null); reload(); }} />
        : <ProfileViewer member={selected} onClose={() => setSelected(null)} />)}
      {claiming && <ClaimProfile member={claiming} onClose={() => setClaiming(null)} onClaimed={async () => { setClaiming(null); await onLogin?.(); }} />}
      {managingAccess && <AccessManager currentMemberId={user?.memberId} onClose={() => setManagingAccess(false)} />}
    </>
  );
}
